import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";

interface GradientActionButtonProps {
  to: string;
  icon: LucideIcon;
  eyebrow: string;
  label: string;
  /** 3-4 hsl(var(--token)) colors */
  gradient: readonly string[];
  /** glow shadow color, e.g. "hsl(var(--primary)/0.5)" */
  glow: string;
  className?: string;
  fullWidth?: boolean;
}

export const GradientActionButton = ({
  to,
  icon: Icon,
  eyebrow,
  label,
  gradient,
  glow,
  className = "",
  fullWidth = false,
}: GradientActionButtonProps) => {
  const stops = [...gradient, gradient[0]].join(", ");
  return (
    <Link to={to} className={fullWidth ? "block w-full" : "inline-block"}>
      <button
        type="button"
        className={`group relative inline-flex min-h-[48px] ${
          fullWidth ? "w-full" : "w-full sm:w-auto"
        } items-center justify-start gap-3 overflow-hidden rounded-xl border border-white/10 px-4 py-2.5 text-left text-primary-foreground transition-all duration-300 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${className}`}
        style={{
          background: `linear-gradient(135deg, ${stops})`,
          backgroundSize: "300% 300%",
          animation: "gradient-shift 8s ease-in-out infinite",
          boxShadow: `0 0 22px ${glow}`,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 38px ${glow}`;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 22px ${glow}`;
        }}
      >
        <span className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background/15 backdrop-blur-sm">
          <Icon className="h-4 w-4 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6" />
        </span>
        <span className="relative flex min-w-0 flex-col">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">
            {eyebrow}
          </span>
          <span className="text-sm font-semibold leading-tight">{label}</span>
        </span>
      </button>
    </Link>
  );
};

// Curated per-button palettes (each different)
export const BUTTON_PALETTES = {
  community: {
    gradient: ["hsl(340 78% 52%)", "hsl(12 88% 55%)", "hsl(30 95% 56%)"],
    glow: "hsl(340 78% 52%/0.5)",
  },

  messages: {
    gradient: ["hsl(268 78% 58%)", "hsl(292 76% 56%)", "hsl(315 76% 55%)"],
    glow: "hsl(285 78% 58%/0.5)",
  },
  movieNight: {
    gradient: ["hsl(330 81% 60%)", "hsl(25 95% 58%)", "hsl(45 100% 65%)"],
    glow: "hsl(25 95% 58%/0.55)",
  },
  collections: {
    gradient: ["hsl(348 58% 34%)", "hsl(0 62% 26%)", "hsl(12 55% 32%)"],
    glow: "hsl(350 60% 32%/0.5)",
  },
} as const;
